/**
 * Merges SaaS plan ceilings with firm module_states via resolve_firm_access_context RPC.
 * SaaS Admin sets ceilings; Firm Admin toggles within them; runtime honors both.
 */
import { dataProvider as supabase } from "@/lib/providers/data-provider";
import type { ERPModuleKey } from "@/lib/module-store";

export type FirmAccessContext = {
  ok: boolean;
  firmId: string | null;
  modules: Record<string, boolean>;
  features: Record<string, boolean>;
};

const CACHE_MS = 60_000;
let cached: FirmAccessContext | null = null;
let cachedAt = 0;

function parseContext(raw: unknown): FirmAccessContext {
  if (!raw || typeof raw !== "object") {
    return { ok: false, firmId: null, modules: {}, features: {} };
  }
  const row = raw as Record<string, unknown>;
  const modules =
    row.modules && typeof row.modules === "object"
      ? Object.fromEntries(
          Object.entries(row.modules as Record<string, unknown>).map(([k, v]) => [
            k,
            v !== false && v !== "false" && v !== 0,
          ]),
        )
      : {};
  const features =
    row.features && typeof row.features === "object"
      ? Object.fromEntries(
          Object.entries(row.features as Record<string, unknown>).map(([k, v]) => [
            k,
            v !== false && v !== "false" && v !== 0,
          ]),
        )
      : {};
  return {
    ok: row.ok === true,
    firmId: typeof row.firm_id === "string" ? row.firm_id : null,
    modules,
    features,
  };
}

export async function fetchFirmAccessContext(opts?: {
  firmId?: string | null;
  force?: boolean;
}): Promise<FirmAccessContext> {
  if (!opts?.force && cached && Date.now() - cachedAt < CACHE_MS) {
    return cached;
  }
  try {
    const { data, error } = await supabase.rpc(
      "resolve_firm_access_context" as never,
      (opts?.firmId ? { p_firm_id: opts.firmId } : {}) as never,
    );
    if (!error && data) {
      cached = parseContext(data);
      cachedAt = Date.now();
      return cached;
    }
  } catch {
    /* RPC may be unavailable before migration */
  }
  cached = { ok: false, firmId: null, modules: {}, features: {} };
  cachedAt = Date.now();
  return cached;
}

/** Map plan feature keys (business.orders) to ERP module keys (orders). */
export function planFeatureKeyToModuleKey(featureKey: string): ERPModuleKey | null {
  const key = featureKey.replace(/^business\./, "").trim();
  const map: Record<string, ERPModuleKey> = {
    orders: "orders",
    billing: "billing",
    inventory: "inventory",
    stock: "inventory",
    manufacturing: "manufacturing",
    workshop: "manufacturing",
    barcode: "barcode",
    reports: "reports",
    customer_portal: "customer_portal",
    supplier_portal: "supplier_management",
    karigar_portal: "manufacturing",
    whatsapp: "whatsapp",
    crm: "crm_communications",
    gst: "gst",
    payroll: "payroll",
    attendance: "attendance",
    catalog: "catalog",
  };
  return map[key] ?? null;
}

export function getFirmAccessContextSync(): FirmAccessContext | null {
  return cached;
}
